/**
 * Запись видеосообщения — «кружочка».
 *
 * ── Почему здесь канва, а не поток с камеры напрямую ────────────────────────
 *
 * Telegram ждёт от видеосообщения КВАДРАТ, а камера отдаёт 4:3 или 16:9.
 * Отправить прямоугольник, объявив его кружком, нельзя: получатель увидит
 * обрезанное не по центру видео в круглой маске. Поэтому кадр перерисовывается
 * на квадратную канву с обрезкой по центру, и записывается уже она.
 *
 * ── Про формат ──────────────────────────────────────────────────────────────
 *
 * Перепаковка, в отличие от голосовых, не нужна: Chromium умеет писать
 * MP4/H.264+AAC, а Telegram кружочки принимает именно в нём. Проверено
 * MediaRecorder.isTypeSupported в этом рантайме.
 *
 * ── Что нужно помнить ───────────────────────────────────────────────────────
 *
 * Камера и микрофон глушатся явно во всех ветках, включая размонтирование.
 * Живая дорожка держит индикатор камеры зажжённым, а для приложения, которое
 * работает с рабочими аккаунтами компании, это недопустимо.
 */

import { useEffect, useRef, useState } from 'react';
import { SendIcon, TrashIcon, VideoIcon } from './icons.tsx';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface VideoNoteRecorderProps {
  disabled: boolean;
  /** Готовый квадратный MP4 и сторона квадрата в пикселях. */
  onRecorded: (file: File, side: number) => void | Promise<void>;
}

type Phase = 'idle' | 'recording' | 'preview';

/** Сторона квадрата. 384 — то, что рассылают официальные клиенты. */
const SIDE = 384;

/** Потолок Telegram для видеосообщений. */
const MAX_SECONDS = 60;

export function VideoNoteRecorder({ disabled, onRecorded }: VideoNoteRecorderProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; file: File } | null>(null);
  const [busy, setBusy] = useState(false);

  const liveRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const source = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const frame = useRef<number | null>(null);
  /** Как отменять такт — зависит от того, какой источник кадров выбран. */
  const cancelFrame = useRef<(handle: number) => void>(() => {});
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Отпустить камеру и микрофон. Вызывается из всех веток. */
  const release = (): void => {
    if (frame.current !== null) {
      cancelFrame.current(frame.current);
      frame.current = null;
    }
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    for (const track of source.current?.getTracks() ?? []) track.stop();
    source.current = null;
  };

  useEffect(() => {
    return () => {
      release();
      if (preview) URL.revokeObjectURL(preview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (): Promise<void> => {
    setError(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 640 }, facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      source.current = media;

      const video = document.createElement('video');
      video.srcObject = media;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      liveRef.current = video;

      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Канва не готова');
      canvas.width = SIDE;
      canvas.height = SIDE;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('2D-контекст недоступен');

      /*
       * Обрезка по центру. Берём квадрат со стороной в меньшую сторону кадра —
       * иначе в кружок попал бы растянутый прямоугольник.
       */
      const paint = (): void => {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w > 0 && h > 0) {
          const side = Math.min(w, h);
          ctx.drawImage(video, (w - side) / 2, (h - side) / 2, side, side, 0, 0, SIDE, SIDE);
        }
      };

      /*
       * Такты берутся от КАДРОВ КАМЕРЫ, а не от отрисовки страницы.
       *
       * requestAnimationFrame привязан к перерисовке окна, и в невидимом или
       * задушенном окне он останавливается. Проверено замером: за две секунды
       * в такой ситуации нарисовался ОДИН кадр — то есть кружочек ушёл бы
       * стоп-кадром, и отправитель узнал бы об этом от собеседника.
       *
       * requestVideoFrameCallback вызывается по приходу кадра с камеры и от
       * перерисовки не зависит. Запасной вариант оставлен на случай рантайма
       * без него, но там же лежит и проверка результата ниже — она поймает
       * вырожденную запись независимо от источника тактов.
       */
      const withFrameCallback = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: () => void) => number;
        cancelVideoFrameCallback?: (handle: number) => void;
      };

      if (typeof withFrameCallback.requestVideoFrameCallback === 'function') {
        const tick = (): void => {
          paint();
          frame.current = withFrameCallback.requestVideoFrameCallback!(tick);
        };
        cancelFrame.current = (h) => withFrameCallback.cancelVideoFrameCallback?.(h);
        tick();
      } else {
        const tick = (): void => {
          paint();
          frame.current = requestAnimationFrame(tick);
        };
        cancelFrame.current = (h) => cancelAnimationFrame(h);
        tick();
      }

      // Видео берём с канвы, звук — с исходного потока.
      const canvasStream = canvas.captureStream(30);
      for (const track of media.getAudioTracks()) canvasStream.addTrack(track);

      const mime = MediaRecorder.isTypeSupported('video/mp4;codecs="avc1.42E01E,mp4a.40.2"')
        ? 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"'
        : 'video/mp4';

      chunks.current = [];
      const rec = new MediaRecorder(canvasStream, { mimeType: mime });
      recorder.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => void finish();
      rec.start();

      setSeconds(0);
      setPhase('recording');
      timer.current = setInterval(() => {
        setSeconds((v) => {
          if (v + 1 >= MAX_SECONDS) recorder.current?.stop();
          return v + 1;
        });
      }, 1000);
    } catch (err) {
      release();
      setPhase('idle');
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Доступ к камере не разрешён'
          : 'Не удалось включить камеру',
      );
    }
  };

  const finish = async (): Promise<void> => {
    const recorded = seconds;
    release();

    const blob = new Blob(chunks.current, { type: 'video/mp4' });
    if (blob.size === 0) {
      setPhase('idle');
      setError('Запись не получилась — файл пустой');
      return;
    }

    const url = URL.createObjectURL(blob);

    /*
     * Сверяем длительность файла с той, что показывал таймер.
     *
     * Это защита от вырожденной записи: если кадры не шли, MediaRecorder всё
     * равно отдаёт валидный MP4 — просто из одного кадра. Такой кружок
     * выглядит исправным файлом и отправляется без единой ошибки, а
     * собеседник видит застывшую картинку. Поймать это можно только сверкой.
     */
    const duration = await new Promise<number>((resolve) => {
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.onloadedmetadata = () => resolve(probe.duration);
      probe.onerror = () => resolve(0);
      setTimeout(() => resolve(0), 4000);
      probe.src = url;
    });

    if (recorded >= 1 && duration < recorded / 2) {
      URL.revokeObjectURL(url);
      setPhase('idle');
      setError(
        'Кадры с камеры не шли — записался стоп-кадр. ' +
          'Так бывает, если окно свёрнуто или перекрыто. Повторите с открытым окном.',
      );
      return;
    }

    const file = new File([blob], `video-note-${Date.now()}.mp4`, { type: 'video/mp4' });
    setPreview({ url, file });
    setPhase('preview');
  };

  const discard = (): void => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPhase('idle');
    setSeconds(0);
  };

  const confirm = async (): Promise<void> => {
    if (!preview) return;
    setBusy(true);
    try {
      await onRecorded(preview.file, SIDE);
      discard();
    } catch {
      // Ошибку показывает композер; запись остаётся, чтобы можно было
      // повторить отправку, а не сниматься заново.
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/*
        Канва нужна всегда: captureStream берётся именно с неё, а элемент,
        созданный в момент начала записи, ещё не успел бы попасть в дерево.
        Прячем её от глаз и от скринридера, а не размонтируем.
      */}
      <canvas ref={canvasRef} className="hidden" aria-hidden />

      {phase === 'idle' && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="flex-none text-muted-foreground hover:text-brand"
            disabled={disabled}
            onClick={() => void start()}
            aria-label="Записать видеосообщение"
            title="Записать кружочек"
          >
            <VideoIcon className="size-[18px]" />
          </Button>
          {error && (
            <span role="alert" className="text-xs text-destructive">
              {error}
            </span>
          )}
        </>
      )}

      {phase === 'recording' && (
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1.5">
          <span aria-hidden className="status-dot animate-pulse text-destructive" />
          <span className="flex-none text-sm font-medium tabular-nums text-destructive">
            {format(seconds)}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            Идёт запись видео — не больше минуты
          </span>
          <Button
            type="button"
            size="sm"
            variant="brand"
            className="flex-none"
            onClick={() => recorder.current?.stop()}
          >
            Стоп
          </Button>
        </div>
      )}

      {phase === 'preview' && (
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-2 py-1.5">
          {/* Круглая маска — так же, как увидит собеседник. Прямоугольный
              предпросмотр скрыл бы неудачное кадрирование. */}
          <video
            src={preview?.url}
            controls
            className={cn('size-16 flex-none rounded-full object-cover', busy && 'opacity-50')}
          />
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            Посмотрите перед отправкой — отменить уже нельзя
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="flex-none text-muted-foreground hover:text-destructive"
            onClick={discard}
            disabled={busy}
            aria-label="Удалить запись"
          >
            <TrashIcon className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            className="flex-none"
            onClick={() => void confirm()}
            disabled={busy}
            aria-label="Отправить видеосообщение"
          >
            <SendIcon className="size-4" />
          </Button>
        </div>
      )}
    </>
  );
}

function format(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
